import { LogoutOutlined, SettingOutlined } from "@ant-design/icons";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import type { MenuDataItem } from "@ant-design/pro-components";
import type { RequestConfig, RunTimeLayoutConfig } from "@umijs/max";
import { history, useModel } from "@umijs/max";
import type { MenuProps } from "antd";
import { Avatar, Dropdown, Spin } from "antd";
import React from "react";
import { flushSync } from "react-dom";
import {
  getCurrentUser,
  logout,
  getBootstrapData,
  getSubscription,
} from "@/services/ourwork/auth";
import { SelectLang } from "@/components";
import FeedbackWidget from "@/components/FeedbackWidget";
import defaultSettings from "../config/defaultSettings";
import { errorConfig } from "./requestErrorConfig";
import { renderLucideMenuIcon } from "@/utils/lucide-menu-icon";

const loginPath = "/user/login";
const TOKEN_KEY = "ourwork_token";

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  subscription?: API.Subscription;
  menuTree?: API.RouteItemVO[];
  grantedPermissions?: string[];
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  fetchSubscription?: () => Promise<API.Subscription | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const data = await getCurrentUser();
      if (data && data.id) {
        return data as API.CurrentUser;
      }
    } catch (_error) {
      // auth error
    }
    history.push(loginPath);
    return undefined;
  };

  const fetchSubscription = async () => {
    try {
      return await getSubscription();
    } catch {
      return undefined;
    }
  };

  const fetchBootstrap = async (): Promise<{
    menuTree?: API.RouteItemVO[];
    grantedPermissions?: string[];
  }> => {
    try {
      const data = await getBootstrapData();
      return {
        menuTree: data?.menuTree,
        grantedPermissions: data?.grantedPermissions,
      };
    } catch {
      return {};
    }
  };

  const { location } = history;
  if (
    ![loginPath, "/user/register", "/user/change-initial-password"].includes(
      location.pathname
    )
  ) {
    const currentUser = await fetchUserInfo();

    if (currentUser?.forcePasswordChange) {
      history.push("/user/change-initial-password");
      return {
        fetchUserInfo,
        fetchSubscription,
        currentUser,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    }

    const [subscription, bootstrap] = currentUser
      ? await Promise.all([fetchSubscription(), fetchBootstrap()])
      : [undefined, {}];
    return {
      fetchUserInfo,
      fetchSubscription,
      currentUser,
      subscription,
      menuTree: bootstrap?.menuTree,
      grantedPermissions: bootstrap?.grantedPermissions,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    fetchSubscription,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

/** AvatarDropdown */
const AvatarDropdownWrapper: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { initialState, setInitialState } = useModel("@@initialState");

  if (!initialState?.currentUser) {
    return <Spin size="small" style={{ marginLeft: 8, marginRight: 8 }} />;
  }

  const handleLogout = async () => {
    try {
      await logout();
    } catch (_) {
      // ignore
    }
    localStorage.removeItem(TOKEN_KEY);
    flushSync(() => {
      setInitialState((s) => ({ ...s, currentUser: undefined }));
    });
    const { search, pathname } = window.location;
    if (pathname !== loginPath) {
      history.replace({
        pathname: loginPath,
        search: new URLSearchParams({
          redirect: pathname + search,
        }).toString(),
      });
    }
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "settings",
      icon: <SettingOutlined />,
      label: "設定",
      onClick: () => history.push("/settings"),
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "ログアウト",
      onClick: handleLogout,
    },
  ];

  return <Dropdown menu={{ items: menuItems }}>{children}</Dropdown>;
};

/** Convert backend RouteItemVO to Ant Design Pro MenuDataItem */
function convertToMenuData(items: API.RouteItemVO[]): MenuDataItem[] {
  return items
    .filter((item) => !item.meta?.hidden)
    .map((item) => {
      const visibleChildren = item.children
        ? convertToMenuData(item.children)
        : undefined;
      return {
        path: item.path,
        name: item.meta?.title,
        locale: false,
        icon: item.meta?.icon
          ? renderLucideMenuIcon(item.meta.icon)
          : undefined,
        children:
          visibleChildren && visibleChildren.length > 0
            ? visibleChildren
            : undefined,
      };
    });
}

export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    actionsRender: () => [<SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatarUrl,
      title:
        initialState?.currentUser?.displayName ||
        initialState?.currentUser?.username,
      render: (_: any, avatarChildren: any) => {
        const displayName =
          initialState?.currentUser?.displayName ||
          initialState?.currentUser?.username ||
          "?";
        const firstLetter = displayName.charAt(0).toUpperCase();
        return (
          <AvatarDropdownWrapper>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
              }}
            >
              {initialState?.currentUser?.avatarUrl ? (
                <Avatar size="small" src={initialState.currentUser.avatarUrl} />
              ) : (
                <Avatar
                  size="small"
                  style={{ backgroundColor: "#1677ff", fontSize: 14 }}
                >
                  {firstLetter}
                </Avatar>
              )}
              <span>{displayName}</span>
            </span>
          </AvatarDropdownWrapper>
        );
      },
    },
    footerRender: () => (
      <div
        style={{
          textAlign: "center",
          padding: "16px 0",
          color: "#999",
          fontSize: 14,
        }}
      >
        OurWork &copy; {new Date().getFullYear()} 3plusx Inc.
      </div>
    ),
    onPageChange: () => {
      const { location } = history;
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    menuHeaderRender: undefined,
    menuDataRender: (menuData: MenuDataItem[]) => {
      if (initialState?.menuTree && initialState.menuTree.length > 0) {
        return convertToMenuData(initialState.menuTree);
      }
      return menuData.filter((item) => item.path === "/settings");
    },
    childrenRender: (children: React.ReactNode) => {
      return (
        <>
          {children}
          <FeedbackWidget />
        </>
      );
    },
    ...initialState?.settings,
  };
};

export const request: RequestConfig = {
  ...errorConfig,
};
